import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { formatGameTimer, parseStageTimerSeconds, resolveCountdownElapsedSeconds } from '../../utils/gameTimer';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './letter-loot.css';

const STAGE_NUMBER = 9;
const PARTICIPATION_POINTS = 1;
const LETTERS_MAX = 100;
const WORDS_MAX = 200;
const WORD_ENTRY_MAX = 40;
const DEFAULT_PLAY_SECONDS = 180;
const STAGE6_PLAY_DEADLINE_PREFIX = 'stage6_play_deadline';

function formatStageDescription(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();
}

function stage6PlayDeadlineKey(userId, sessionId) {
  return `${STAGE6_PLAY_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage6PlayDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setPlayDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage6PlayDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage6PlayDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function ClockIcon() {
  return (
    <svg className="s6-timer-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

export default function Stage6({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(6);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage6 = themeData?.stages_data?.stage_9;

  const playSeconds = useMemo(
    () => parseStageTimerSeconds(stage6?.timer, DEFAULT_PLAY_SECONDS),
    [stage6?.timer]
  );

  const logoUrl = s3PublicUrl(stage6?.logo ?? stage6?.badge);
  const rulesList = getStageRulesList(stage6);
  const title = String(stage6?.title ?? stage6?.name ?? 'Stage 6');
  const descriptionText = useMemo(
    () => formatStageDescription(stage6?.description),
    [stage6?.description]
  );
  const descriptionLine = useMemo(
    () => descriptionText.replace(/\s*\n+\s*/g, ' ').trim(),
    [descriptionText]
  );

  const isConfigured = !!stage6 && String(stage6?.title ?? '').trim().length > 0;

  const visuals = useMemo(() => stageThemeVisuals(themeData), [themeData]);
  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's6'), [visuals]);

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const letterInputRef = useRef(null);
  const wordInputRef = useRef(null);
  const playTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);

  const [letterTiles, setLetterTiles] = useState([]);
  const [wordTiles, setWordTiles] = useState([]);
  const [letterDraft, setLetterDraft] = useState('');
  const [wordDraft, setWordDraft] = useState('');
  const [message, setMessage] = useState('');
  const [shakeLetters, setShakeLetters] = useState(false);
  const [shakeWords, setShakeWords] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage6,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const countdownDisplay = timerReady ? formatGameTimer(secondsLeft) : formatGameTimer(playSeconds);
  const inputsLocked = submitted || timerExpired;

  const selectedLetters = letterTiles.join('');
  const createdWords = wordTiles.join('\n');

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(playTimerRef);

    const finishPlayTime = () => {
      clearIntervalTimer(playTimerRef);
      setSecondsLeft(0);
      setTimerExpired(true);
      clearPlayDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startPlayCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setTimerReady(true);
      setSecondsLeft(safeRemaining);
      setTimerExpired(safeRemaining <= 0);

      if (safeRemaining <= 0) {
        finishPlayTime();
        return;
      }

      let remaining = safeRemaining;
      playTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setSecondsLeft(next);
        if (next <= 0) {
          finishPlayTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let playDeadline = readPlayDeadline(user);
    if (!playDeadline) {
      playDeadline = now + playSeconds * 1000;
      setPlayDeadline(user, playDeadline);
    }

    if (now >= playDeadline) {
      setTimerReady(true);
      finishPlayTime();
      return undefined;
    }

    startPlayCountdown((playDeadline - now) / 1000);

    return () => clearIntervalTimer(playTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    playCheckReady,
    playSeconds,
    rulesDismissed,
    submitted,
    user,
    user?.userId,
    user?.sessionId,
  ]);

  const triggerShake = (field) => {
    if (field === 'letters') {
      setShakeLetters(true);
      window.setTimeout(() => setShakeLetters(false), 500);
    } else {
      setShakeWords(true);
      window.setTimeout(() => setShakeWords(false), 500);
    }
  };

  const addLetter = (raw) => {
    const letter = String(raw ?? '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '')
      .slice(0, 1);
    if (!letter || letterTiles.length >= LETTERS_MAX) return false;
    setLetterTiles((prev) => [...prev, letter]);
    if (message) setMessage('');
    return true;
  };

  const handleLetterInput = (ev) => {
    const value = ev.target.value.toUpperCase().replace(/[^A-Z]/g, '');
    if (!value) {
      setLetterDraft('');
      return;
    }
    const letter = value.slice(-1);
    addLetter(letter);
    setLetterDraft('');
  };

  const handleLetterKeyDown = (ev) => {
    if (ev.key === 'Backspace' && letterDraft === '' && letterTiles.length > 0) {
      ev.preventDefault();
      setLetterTiles((prev) => prev.slice(0, -1));
      if (message) setMessage('');
    }
  };

  const addWord = (raw) => {
    const word = String(raw ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, WORD_ENTRY_MAX);
    if (!word) return false;

    const nextText = [...wordTiles, word].join('\n');
    if (nextText.length > WORDS_MAX) return false;

    setWordTiles((prev) => [...prev, word]);
    if (message) setMessage('');
    return true;
  };

  const handleWordKeyDown = (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      if (addWord(wordDraft)) {
        setWordDraft('');
      }
    }
    if (ev.key === 'Backspace' && wordDraft === '' && wordTiles.length > 0) {
      ev.preventDefault();
      setWordTiles((prev) => prev.slice(0, -1));
      if (message) setMessage('');
    }
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;

    const letters = String(selectedLetters ?? '').trim();
    const words = String(createdWords ?? '').trim();

    if (!auto && !letters) {
      triggerShake('letters');
      setMessage('Please enter all the letters you selected');
      letterInputRef.current?.focus();
      return;
    }
    if (!auto && !words) {
      triggerShake('words');
      setMessage('Please enter all the words you created');
      wordInputRef.current?.focus();
      return;
    }

    const playDeadline = readPlayDeadline(user);
    const stageTimeSeconds = resolveCountdownElapsedSeconds({
      totalSeconds: playSeconds,
      secondsLeft,
      deadlineMs: playDeadline,
    });

    clearIntervalTimer(playTimerRef);
    clearPlayDeadline(user);

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const finalPoints = PARTICIPATION_POINTS;
    const trimmedAnswer = JSON.stringify({ letters, words });

    if (isDemoSubscribeRedirectUser(user)) {
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: true,
        });
      }
      return;
    }

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
      });
    } catch (err) {
      setSubmitted(false);
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: true,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  const canSubmit =
    String(selectedLetters ?? '').trim() !== '' && String(createdWords ?? '').trim() !== '';

  return (
    <div className="stage6-page" style={pageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 9 is not configured: set stage_9 title and description in the theme.
          </p>
        }
      >
        <>
          <div className="stage-page-main stage6-header-main">
            <header className="s6-top-bar">
              <div
                className={`s6-top-timer${timerExpired ? ' s6-top-timer--expired' : ''}`}
                aria-label="Stage timer"
                aria-live="polite"
              >
                <ClockIcon />
                <span className="s6-timer-value">{countdownDisplay}</span>
              </div>
              {rulesList.length > 0 ? (
                <button
                  type="button"
                  className="stage6-rules-fab"
                  onClick={openRules}
                  aria-label="How to play"
                  title="How to play"
                >
                  ?
                </button>
              ) : null}
            </header>

            <header className="stage6-hero">
              <div className="stage6-logo-wrap">
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="stage6-logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <div className="stage6-logo-fallback" aria-hidden>
                    Stage 6
                  </div>
                )}
              </div>
              <h1 className="stage6-title">{title}</h1>
            </header>

            {descriptionLine ? (
              <p className="stage6-description" title={descriptionLine}>
                {descriptionLine}
              </p>
            ) : null}
          </div>

          <div className="s6-play-area">
              {!submitted ? (
                <div className="s6-game-layout">
                <section
                  className={`s6-panel s6-panel--letters${shakeLetters ? ' s6-panel--shake' : ''}`}
                >
                  <h2 className="s6-panel-title">Letter Bank</h2>
                  <div className="s6-tile-grid s6-tile-grid--letters" aria-live="polite">
                    {letterTiles.map((letter, index) => (
                      <span key={`letter-${index}-${letter}`} className="s6-tile s6-tile--letter">
                        {letter}
                      </span>
                    ))}
                  </div>
                  <label className="s6-entry-label" htmlFor="stage6-letter-input">
                    Type a letter
                  </label>
                  <input
                    ref={letterInputRef}
                    id="stage6-letter-input"
                    type="text"
                    className="s6-letter-input"
                    value={letterDraft}
                    onChange={handleLetterInput}
                    onKeyDown={handleLetterKeyDown}
                    maxLength={1}
                    inputMode="text"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder="A"
                    disabled={inputsLocked || letterTiles.length >= LETTERS_MAX}
                    aria-label="Enter one letter"
                  />
                </section>

                <div className="s6-arrow" aria-hidden>
                  <i className="fa-solid fa-arrow-right" />
                </div>

                <section
                  className={`s6-panel s6-panel--words${shakeWords ? ' s6-panel--shake' : ''}`}
                >
                  <h2 className="s6-panel-title">Words Found</h2>
                  <div className="s6-tile-grid s6-tile-grid--words" aria-live="polite">
                    {wordTiles.map((word, index) => (
                      <span key={`word-${index}-${word}`} className="s6-tile s6-tile--word">
                        {word}
                      </span>
                    ))}
                  </div>
                  <label className="s6-entry-label" htmlFor="stage6-word-input">
                    Type a word and press Enter
                  </label>
                  <input
                    ref={wordInputRef}
                    id="stage6-word-input"
                    type="text"
                    className="s6-word-input"
                    value={wordDraft}
                    onChange={(ev) => {
                      setWordDraft(ev.target.value.slice(0, WORD_ENTRY_MAX));
                      if (message) setMessage('');
                    }}
                    onKeyDown={handleWordKeyDown}
                    maxLength={WORD_ENTRY_MAX}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="HAM"
                    disabled={inputsLocked}
                    aria-label="Enter a word"
                  />
                </section>

                <div
                  className="s6-panel s6-panel--puzzle"
                  aria-label="Puzzle pieces progress"
                >
                  <StagePuzzleReveal themeData={themeData} />
                </div>
                </div>
              ) : (
                <p className="stage6-complete">Thanks! Your response has been submitted.</p>
              )}

              {!submitted ? (
                <form className="s6-submit-wrap" onSubmit={handleSubmit}>
                  <button type="submit" className="stage6-submit" disabled={inputsLocked || !canSubmit}>
                    Submit
                  </button>
                </form>
              ) : null}

              {message ? <div className="stage6-hint">{message}</div> : null}
          </div>

          {showRevealOverlay ? (
            <div
              className="stage-reveal-overlay"
              role="dialog"
              aria-modal="true"
              aria-label="Revealing your puzzle progress"
              onClick={handleRevealOverlayClick}
            >
              <div className="stage-reveal-confetti" aria-hidden>
                {revealConfettiPieces.map((piece) => (
                  <span
                    key={piece.id}
                    className="stage-reveal-confetti-piece"
                    style={{
                      left: piece.left,
                      animationDelay: piece.delay,
                      animationDuration: piece.duration,
                    }}
                  />
                ))}
              </div>
              <div className="stage-reveal-stage">
                <aside className="stage-reveal-panel" aria-label="Puzzle pieces progress">
                  <StagePuzzleReveal
                    themeData={themeData}
                    completedDaysOverride={revealCompletedDays}
                    animateReveal
                  />
                </aside>
              </div>
            </div>
          ) : null}
        </>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage6-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage6-rules-title"
          onClick={closeRules}
        >
          <div className="stage6-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage6-rules-title">How to play</h2>
            <ul className="stage6-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage6-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
